import { type ApiSchemas, call } from '@rental/contracts/client'
import { format, formatDate, plural } from '@rental/i18n'
import { Button, cn, Field } from '@rental/ui'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { api, load } from '../api'
import { ProblemNotice, StaffInput } from '../components/ui'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'

export type Place = ApiSchemas['CargosPlace']

/** How a place reads: "NAPOLI (NA)", or a state's name. */
export function placeName(place: Place, state: string): string {
  return place.province === 'ES' ? `${place.name} (${state})` : `${place.name} (${place.province})`
}

/**
 * S6: a place from CaRGOS's own tables (comuni and states), picked by name: a
 * search box and a list to choose from. No free text reaches the record: CaRGOS
 * takes its codes only.
 *
 * UI-1e, by keyboard: each result is a button, named with its place and, for a
 * place that has gone, since when; Tab moves through them and nothing is
 * chosen on the way. One status says the search's state as it changes. Chosen,
 * the place shows with "Cambia", named by its field; the focus goes to it, and
 * from it back to the search.
 */
export function PlacePicker({
  label,
  kind,
  on,
  value,
  onChange,
  error,
  required = true,
  className,
}: {
  label: string
  kind: 'comune' | 'state' | 'any'
  /** The day the place must exist on: a residence's. Birth and issuing places may have gone since. */
  on?: string
  value: Place | null
  onChange: (place: Place | null) => void
  error?: string | undefined
  required?: boolean
  className?: string
}) {
  const { locale, messages: m } = useLocale()
  const labelId = useId()
  const changeId = useId()
  const valueId = useId()
  const errorId = useId()
  const area = useRef<HTMLDivElement>(null)
  const [typed, setTyped] = useState('')
  const [query, setQuery] = useState('')
  // A query per pause in typing, not per key.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(typed.trim()), 250)
    return () => clearTimeout(timer)
  }, [typed])
  const results = useQuery({
    queryKey: ['cargos-places', kind, on ?? null, query],
    enabled: query.length >= 2 && value === null,
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/cargos/places', {
            params: { query: { q: query, kind, ...(on ? { on } : {}) } },
          }),
        ),
      ),
    staleTime: Number.POSITIVE_INFINITY,
  })

  // The list or the place gives way to the other: the focus goes with it, never to the page.
  const settle = (change: () => void, target: string) => {
    flushSync(change)
    area.current?.querySelector<HTMLElement>(target)?.focus()
  }
  const choose = (place: Place) => settle(() => onChange(place), 'button')
  const reopen = () =>
    settle(() => {
      setTyped('')
      setQuery('')
      onChange(null)
    }, 'input')

  if (value) {
    return (
      <div ref={area} className={cn('flex flex-col gap-1', className)}>
        <span id={labelId} className="text-sm font-medium text-text">
          {label}
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <span id={valueId} className="text-base text-text">
            {placeName(value, m.places.state)}
          </span>
          <Button
            id={changeId}
            variant="outline"
            className="rounded-sm"
            // "Cambia" and the field it changes; the place, and its error, as its description.
            aria-labelledby={`${changeId} ${labelId}`}
            aria-describedby={error ? `${valueId} ${errorId}` : valueId}
            onClick={reopen}
          >
            {m.places.change}
          </Button>
        </div>
        {error ? (
          <p id={errorId} className="text-sm font-medium text-danger">
            {error}
          </p>
        ) : null}
      </div>
    )
  }

  const searched = query.length >= 2
  const items = searched && results.isSuccess ? results.data.items : []
  const status =
    !searched || results.isError
      ? ''
      : results.isPending
        ? m.places.searching
        : items.length === 0
          ? m.places.none
          : plural(locale, m.places.found, items.length)
  return (
    <div ref={area} className={cn('flex flex-col gap-2', className)}>
      <Field label={label} hint={m.places.searchHint} error={error} required={required}>
        {(control) => (
          <StaffInput
            {...control}
            type="search"
            autoComplete="off"
            maxLength={60}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        )}
      </Field>
      {/* One status, kept in place: searching, then how many places, or none. */}
      <p role="status" className={status ? 'text-sm text-text-muted' : 'sr-only'}>
        {status}
      </p>
      {/* A search that failed says so: "nessun luogo" would be an answer it never got. */}
      {searched && results.isError ? <ProblemNotice problem={problemOf(results.error)} /> : null}
      {items.length > 0 ? (
        <ul aria-label={label} className="flex flex-col gap-1">
          {items.map((place) => (
            <li key={place.code}>
              <button
                type="button"
                onClick={() => choose(place)}
                className={cn(
                  'flex min-h-touch w-full flex-wrap items-center gap-x-3 rounded-sm border border-text-muted px-4 text-start transition-colors hover:bg-surface-well',
                  place.endsOn ? 'text-text-muted' : 'text-text',
                )}
              >
                <span>{placeName(place, m.places.state)}</span>
                {place.endsOn ? (
                  <>
                    {' '}
                    <span className="text-sm">
                      {format(m.places.gone, { date: formatDate(locale, place.endsOn) })}
                    </span>
                  </>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
